import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useLang } from '../lib/useLang'
import { RoutesEnum } from '../lib/routes'
import './Landing.css'
import './PublicPage.css'

export default function UseCases() {
  const navigate = useNavigate()
  const { t } = useLang()
  const useCases = t('useCases')

  useEffect(() => {
    window.scrollTo(0, 0)
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible')
            observer.unobserve(e.target)
          }
        }),
      { threshold: 0.08 }
    )
    document.querySelectorAll('[data-reveal]').forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  // Per-card cursor spotlight — direct DOM updates via CSS variables, no re-render
  const handleUseCaseMouseMove = (e) => {
    const card = e.currentTarget
    const rect = card.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    card.style.setProperty('--mx', `${x}%`)
    card.style.setProperty('--my', `${y}%`)
  }

  return (
    <div className='public-page usecases-page'>
      <Helmet>
        <title>{useCases?.title} — MODELLER</title>
        <meta
          name='description'
          content={useCases?.subtitle || 'Business use cases for Modeller — concrete tasks where ML pays off.'}
        />
      </Helmet>

      {/* Hero */}
      <div className='public-hero'>
        <div className='public-hero-inner'>
          <div data-reveal className='public-tag'>{useCases?.tag}</div>
          <h1 data-reveal className='public-hero-title' style={{ transitionDelay: '0.1s' }}>
            {useCases?.title}
          </h1>
          <p data-reveal className='public-hero-sub' style={{ transitionDelay: '0.2s' }}>
            {useCases?.subtitle}
          </p>
        </div>
      </div>

      {/* Cards */}
      <div className='public-content usecases-content'>
        <div className='public-content-inner usecases-page-inner'>
          <div className='usecases-grid'>
            {Array.isArray(useCases?.items) && useCases.items.map((uc, i) => (
              <article
                key={uc.icon || i}
                data-reveal
                className='usecase-card'
                style={{ transitionDelay: `${i * 0.08}s` }}
                onMouseMove={handleUseCaseMouseMove}
              >
                <div className='usecase-card-spotlight' aria-hidden='true' />
                <div className='usecase-card-inner'>
                  <div className='usecase-icon-wrap'>
                    <div className='usecase-icon-glow' aria-hidden='true' />
                    <img
                      src={`/images/usecases/${uc.icon}.svg`}
                      alt=''
                      className='usecase-icon'
                      loading='lazy'
                    />
                  </div>
                  <div className='usecase-card-body'>
                    <span className='usecase-tag'>{uc.tag}</span>
                    <h3 className='usecase-card-title'>{uc.title}</h3>
                    {uc.client && (
                      <div className='usecase-client'>{uc.client}</div>
                    )}
                    <p className='usecase-card-desc'>{uc.description}</p>
                  </div>
                  <div className='usecase-metric'>
                    <span className='usecase-metric-dot' aria-hidden='true' />
                    <span>{uc.metric}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* CTA */}
          <div data-reveal className='usecases-cta' style={{ transitionDelay: '0.3s' }}>
            <p className='usecases-cta-text'>{t('useCases.ctaText')}</p>
            <div className='flex flex-wrap justify-center gap-4'>
              <button onClick={() => navigate(RoutesEnum.CONTACT)} className='header-button-primary landing-btn-shimmer'>
                {t('nav.getStarted')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
